import React, { Children } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { FlaskConical, Truck, ShieldCheck, Star } from 'lucide-react';
import { CTA } from './CTA';
import { Vial } from './Vial';
import { UKFlag } from './UKFlag';
const ENTRANCE_EASE = [0.22, 1, 0.36, 1] as const;
const PILLARS = [{
  icon: FlaskConical,
  title: 'Third-party tested',
  body: 'HPLC verified every batch'
}, {
  icon: Truck,
  title: 'Same-day UK dispatch',
  body: 'Tracked cold-chain delivery'
}, {
  icon: ShieldCheck,
  title: '30-day guarantee',
  body: 'Re-shipped if compromised'
}];
export function Hero() {
  const reduce = useReducedMotion();
  // Stagger every text element in the left column for a single orchestrated reveal.
  const copyContainer = {
    hidden: {},
    visible: {
      transition: reduce ? {} : {
        staggerChildren: 0.07,
        delayChildren: 0.05
      }
    }
  };
  const copyItem = {
    hidden: reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.55,
        ease: ENTRANCE_EASE
      }
    }
  };
  return <section className="relative w-full overflow-hidden" data-id="element-287">
      <div className="container mx-auto pt-14 lg:pt-20 pb-20 lg:pb-28" data-id="element-288">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-12 items-center" data-id="element-289">
          {/* Copy — staggered children */}
          <motion.div className="lg:col-span-7" variants={copyContainer} initial="hidden" animate="visible" data-id="element-290">
            {/* Eyebrow */}
            <motion.div variants={copyItem} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card mb-7" data-id="element-291">
              <UKFlag size={14} data-id="element-292" />
              <span className="text-[11px] font-medium tracking-wide uppercase text-muted-foreground" data-id="element-293">
                Shipped from the UK
                <span className="text-border mx-1.5" data-id="element-294">·</span>
                HPLC tested
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1 variants={copyItem} className="font-semibold tracking-[-0.035em] text-foreground text-[42px] leading-[1.02] sm:text-[56px] lg:text-[74px]" data-id="element-295">
              Lab-grade <span className="text-primary" data-id="element-296">peptides</span>
              <span className="text-primary" data-id="element-297">,</span>
              <br data-id="element-298" />
              <span className="text-foreground" data-id="element-299">purity in every </span>
              <span className="text-primary" data-id="element-300">.ml</span>
            </motion.h1>

            {/* Subhead */}
            <motion.p variants={copyItem} className="mt-6 max-w-[560px] text-[17px] leading-[1.55] text-muted-foreground" data-id="element-301">
              Every batch independently HPLC verified at{' '}
              <span className="font-mono text-foreground" data-id="element-302">≥99%</span> purity by
              an ISO 17025 accredited UK lab. Same-day dispatch, cold-chain,
              discreet packaging.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={copyItem} className="mt-9 flex flex-wrap items-center gap-3" data-id="element-303">
              <CTA to="/shop" variant="primary" size="lg" data-id="element-304">
                Shop peptides
              </CTA>
              <CTA href="#lab" variant="secondary" size="lg" data-id="element-305">
                View lab results
              </CTA>
            </motion.div>

            {/* Trustpilot indicator */}
            <motion.div variants={copyItem} className="mt-8 flex items-center gap-3" data-id="element-306">
              <div className="flex items-center gap-0.5" aria-label="4.8 out of 5" data-id="element-307">
                {Array.from({
                length: 5
              }).map((_, i) => <motion.span key={i} initial={reduce ? {
                opacity: 0
              } : {
                opacity: 0,
                scale: 0.4
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.35,
                delay: 0.55 + i * 0.06,
                ease: ENTRANCE_EASE
              }} data-id="element-308">
                    <Star className="h-4 w-4 fill-primary text-primary" strokeWidth={0} data-id="element-309" />
                  </motion.span>)}
              </div>
              <span className="text-[13px] text-muted-foreground" data-id="element-310">
                <span className="font-mono text-foreground" data-id="element-311">4.8</span>
                <span className="text-border mx-1.5" data-id="element-312">·</span>
                <span className="font-mono text-foreground" data-id="element-313">1,247</span> reviews
                on{' '}
                <span className="font-semibold text-foreground" data-id="element-314">
                  Trustpilot
                </span>
              </span>
            </motion.div>
          </motion.div>

          {/* Visual — composed reveal: backdrop → vial → annotations */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="lg:col-span-5" data-id="element-315">
            <div className="relative w-full max-w-[460px] mx-auto" data-id="element-316">
              {/* Soft tonal backdrop — scales in subtly */}
              <motion.div initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.7,
              ease: ENTRANCE_EASE
            }} className="absolute inset-x-6 top-6 bottom-10 rounded-[28px] bg-secondary/60 origin-center" data-id="element-317" />
              <motion.div initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              scale: 0.97
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.75,
              delay: 0.08,
              ease: ENTRANCE_EASE
            }} className="absolute inset-x-12 top-14 bottom-20 rounded-[22px] bg-card border border-border origin-center" data-id="element-318" />

              {/* Vial */}
              <div className="relative flex items-end justify-center pt-12 pb-12" data-id="element-319">
                {/* Outer wrapper — handles entrance only via framer-motion */}
                <motion.div initial={reduce ? {
                opacity: 0
              } : {
                opacity: 0,
                y: -28,
                scale: 0.94
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} transition={{
                duration: 0.85,
                delay: 0.2,
                ease: ENTRANCE_EASE
              }} className="relative text-foreground" data-id="element-320">
                  {/* Float-sway via pure CSS — runs infinitely, independent of
                   framer-motion's entrance state. */}
                  <div className="animate-vial-float" data-id="element-321">
                    <Vial label="BPC-157" size={340} data-id="element-322" />
                  </div>

                  {/* One-shot light sweep across the glass — subtle reveal cue */}
                  {!reduce && <motion.span aria-hidden initial={{
                  x: '-180%',
                  opacity: 0
                }} animate={{
                  x: '180%',
                  opacity: [0, 0.5, 0]
                }} transition={{
                  duration: 1.4,
                  delay: 0.95,
                  ease: 'easeOut'
                }} className="pointer-events-none absolute inset-y-6 left-1/2 -translate-x-1/2 w-[55%] -skew-x-12" style={{
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)',
                  mixBlendMode: 'overlay'
                }} data-id="element-323" />}
                </motion.div>

                {/* Annotation: purity (top-right) — connector draws, card fades */}
                <div className="hidden sm:flex absolute top-8 right-0 lg:right-2 items-center gap-3" data-id="element-324">
                  <motion.span aria-hidden initial={reduce ? {
                  opacity: 0
                } : {
                  scaleX: 0,
                  opacity: 0
                }} animate={{
                  scaleX: 1,
                  opacity: 1
                }} transition={{
                  duration: 0.5,
                  delay: 1.05,
                  ease: ENTRANCE_EASE
                }} className="hidden md:block h-px w-10 bg-foreground/25 origin-right" data-id="element-325" />
                  <motion.div initial={reduce ? {
                  opacity: 0
                } : {
                  opacity: 0,
                  x: 14
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.55,
                  delay: 1.15,
                  ease: ENTRANCE_EASE
                }} className="rounded-lg bg-background border border-border px-3 py-2 shadow-sm" data-id="element-326">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-327">
                      Purity
                    </div>
                    <div className="font-mono text-base text-foreground font-medium leading-none mt-0.5" data-id="element-328">
                      99.7%
                    </div>
                  </motion.div>
                </div>

                {/* Annotation: batch (bottom-left) */}
                <div className="hidden sm:flex absolute bottom-14 left-0 lg:left-2 items-center gap-3" data-id="element-329">
                  <motion.div initial={reduce ? {
                  opacity: 0
                } : {
                  opacity: 0,
                  x: -14
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.55,
                  delay: 1.25,
                  ease: ENTRANCE_EASE
                }} className="rounded-lg bg-background border border-border px-3 py-2 shadow-sm" data-id="element-330">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-331">
                      Batch
                    </div>
                    <div className="font-mono text-[13px] text-foreground font-medium leading-none mt-0.5" data-id="element-332">
                      BP-24091
                    </div>
                  </motion.div>
                  <motion.span aria-hidden initial={reduce ? {
                  opacity: 0
                } : {
                  scaleX: 0,
                  opacity: 0
                }} animate={{
                  scaleX: 1,
                  opacity: 1
                }} transition={{
                  duration: 0.5,
                  delay: 1.35,
                  ease: ENTRANCE_EASE
                }} className="hidden md:block h-px w-10 bg-foreground/25 origin-left" data-id="element-333" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll cue — gently invites the user down without nagging */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 1.5
      }} className="hidden lg:flex justify-center mt-16" aria-hidden data-id="element-334">
          <div className="flex flex-col items-center gap-2" data-id="element-335">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium" data-id="element-336">
              Scroll
            </span>
            <motion.span animate={reduce ? undefined : {
            y: [0, 6, 0],
            opacity: [0.35, 0.85, 0.35]
          }} transition={reduce ? undefined : {
            duration: 2.2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="block h-6 w-px bg-foreground origin-top" data-id="element-337" />
          </div>
        </motion.div>
      </div>
    </section>;
}